// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as Immutable from 'immutable';

// Utils
import { getAllCellsBetween } from '../../../../../../element/table/utils/tableCellSelectionUtils';
import {
    getElementId,
    getTableContentCellTextStyle,
} from '../../../../../../../common/elements/utils/elementPropertyUtils';
import { getCurrentCellSelections } from '../../../../../../element/table/tableSelector';
import { getElementDefaultColors } from '../../editorTools/textStylePopup/textStylePopupUtils';

// Components
import DraftTextColorButton from './DraftTextColorButton';
import TextStylePopupContent from '../../editorTools/textStylePopup/TextStylePopupContent';

// Constants
import {
    TEXT_BACKGROUND_PRESETS,
    TEXT_COLOR_PRESETS,
} from '../../../../../../components/editor/plugins/textColor/textColorConstants';

// Styles
import HotTableManager from '../../../../../../element/table/manager/HotTableManager';

const TextStyleRichTextToolPopupContentComponent = ({ showBlockStyleTools, ...rest }) => {
    const { defaultTextColor, defaultBackgroundColor } = getElementDefaultColors(rest.element) || {};

    return (
        <TextStylePopupContent
            textColorComponents={[
                <DraftTextColorButton
                    key="default"
                    {...rest}
                    preset={null}
                    customTextColor={defaultTextColor}
                    customBackgroundColor={defaultBackgroundColor}
                />,
                ...Object.values(TEXT_COLOR_PRESETS).map((preset) => (
                    <DraftTextColorButton
                        key={preset}
                        {...rest}
                        preset={preset}
                        customBackgroundColor={defaultBackgroundColor}
                    />
                )),
            ]}
            textHighlightComponents={Object.values(TEXT_BACKGROUND_PRESETS).map((preset) => (
                <DraftTextColorButton key={preset} {...rest} preset={preset} />
            ))}
        />
    );
};

TextStyleRichTextToolPopupContentComponent.propTypes = {
    blockType: PropTypes.string,
    onTextStyleClick: PropTypes.func,
    showBlockStyleTools: PropTypes.bool,
};

/*****************************************************
 * Create enhancer for when a table grid is focused
 */

const mapTableGridStateToProps = (state, ownProps) => {
    const { element } = ownProps;

    const currentCellSelections = getCurrentCellSelections(state, ownProps);

    const selectedCellsCoords = getAllCellsBetween(currentCellSelections);
    const firstSelectedCellCoords = selectedCellsCoords.shift();

    if (!firstSelectedCellCoords) return { currentStyle: Immutable.OrderedSet() };

    const firstSelectedCellTextStyles = Immutable.OrderedSet(
        getTableContentCellTextStyle(firstSelectedCellCoords)(element),
    );

    const currentStyle = selectedCellsCoords.reduce(
        (acc, cellCoords) => acc.intersect(getTableContentCellTextStyle(cellCoords)(element)),
        firstSelectedCellTextStyles,
    );

    return { currentStyle };
};

const mapTableGridDispatchToProps = (dispatch, ownProps) => ({
    onTextColorClick: (colorStyle) => {
        const { element } = ownProps;
        const hotTableInstance = HotTableManager.getHotTableComponent(getElementId(element));

        hotTableInstance.tableOperationsRef.current.applyTextStyle(colorStyle);
    },
});

const enhanceWithTableGridState = connect(mapTableGridStateToProps, mapTableGridDispatchToProps);
const TableGridTextStyleRichTextToolPopupContentComponent = enhanceWithTableGridState(
    TextStyleRichTextToolPopupContentComponent,
);

/***************************************************************
 * Choose which enhancer to use depending on current editor key
 */

const TextStyleRichTextToolPopupContent = (props) => {
    const { item } = props;
    const showBlockStyleTools = item?.toolSettings?.showBlockStyleTools || props.showBlockStyleTools;

    // TODO-REMOVE-DRAFT - Tables are the only place where we use the DraftJS editor now
    return <TableGridTextStyleRichTextToolPopupContentComponent {...props} showBlockStyleTools={showBlockStyleTools} />;
};

TextStyleRichTextToolPopupContent.propTypes = {
    showBlockStyleTools: PropTypes.bool,
    editorKey: PropTypes.string,
    item: PropTypes.object,
};

export default TextStyleRichTextToolPopupContent;
